import useChartGrouping from '@/composables/chart-grouping';
import useChartProps from '@/composables/grid-page/chart/chart-props';
import useDebounce from 'shared/composables/debounce';
import useGoals from '@/composables/goal/goals';
import useParamDefaulter from '@/composables/view-params/default';
import useParamEnricher from '@/composables/view-params/enrich';
import useProperties from '@/composables/property/property';
import usePropertyNumericChartPath from '@/composables/property-numeric-chart-path';
import { AND, CREATED_AT, EQ, HAS, MODIFIED_AT, NOT, RESULT, TYPE, UID } from 'shared/api/query/constants';
import { DateTime } from 'luxon';
import { GoalActivityFilterHandler } from '@/lib/filter/goal-activity/handler';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { TIME_SERIES_AGGREGATORS } from '@/lib/constants';
import { UNASSIGNED_VALUE } from '@/composables/grid-page/chart/utils';
import { UpdatesFilterHandler } from '@/lib/filter/updates/handler';
import { aggregatorType, chartGroupByOption, customFunc, gridPageChartType, timeSeriesInterval } from 'shared/constants.json';
import { buildCustomFuncProgressCourseListPred } from '@/api/query/custom-func-helper';
import { combine, notHasFilter } from 'shared/api/query/filter';
import { computed, ref, shallowRef, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { createGoalCycleFilter } from '@/api/query/nebula/goal-cascade';
import { dogma } from '@/api';
import { goalActivity as goalActivityConfig, goal as goalConfig, update as updateConfig } from 'shared/api/query/configs.json';
import { goalsInCycleVarBlock } from '@/api/query/nebula/goal';
import { logCatch } from '@/lib/logger/logger';

// Goals inside a planning are unpublished (publishedAt == null). Charts always
// hide them — same convention as `usePublishedAtFilter` / GoalsExplorer.
const excludePlanningGoalsFilter = { func: { name: HAS, attr: goalConfig.edges.publishedAt } };

const generateTimeSeries = (startDate, endDate, interval = timeSeriesInterval.weekly, now = DateTime.local()) => {
  const intervalConfig = { weeks: 0, months: 0 };

  switch (interval) {
    case timeSeriesInterval.monthly:
      intervalConfig.months = 1;
      break;
    case timeSeriesInterval.daily:
      intervalConfig.days = 1;
      break;
    case timeSeriesInterval.weekly:
    default:
      intervalConfig.weeks = 1;
      break;
  }

  const timeSeries = [];
  let t = startDate;

  if (now.diff(t) < 0) {
    return [t];
  }

  while (t.diff(endDate) < 0 && t.diff(now) < 0) {
    timeSeries.push(t);
    t = t.plus(intervalConfig);
  }

  if (now.diff(endDate) < 0) {
    timeSeries.push(now);
  }

  if (now.diff(endDate) > 0) {
    timeSeries.push(endDate);
  }

  return timeSeries;
};

export default function useChartDataQuery(chart) {
  const loading = ref(false);
  // Shallow: the raw dogma response is read-only plain data. Deep reactivity
  // would walk the (potentially large) tree on every access.
  const resultRaw = shallowRef(null);
  const error = ref(null);
  // Anchor for time-series ranges. Kept stable across re-queries so minor
  // timestamp drift doesn't bust downstream reactive chains; refreshed only
  // after STABLE_NOW_TTL_MS so long-lived dashboards don't drift past midnight.
  const stableNow = ref(DateTime.local());
  const STABLE_NOW_TTL_MS = 10 * 60 * 1000;
  const refreshStableNow = () => {
    const now = DateTime.local();
    if (now.toMillis() - stableNow.value.toMillis() >= STABLE_NOW_TTL_MS) {
      stableNow.value = now;
    }
  };

  const { debounce } = useDebounce({ onBeforeUnmountCancel: true });

  const { processChartData } = useChartGrouping(chart, resultRaw);

  const goalFilterHandler = new GoalFilterHandler();
  const updatesFilterHandler = new UpdatesFilterHandler();
  const goalActivityFilterHandler = new GoalActivityFilterHandler();

  const propertySvc = useProperties();
  const goalsSvc = useGoals();
  const paramEnricher = useParamEnricher(propertySvc, goalsSvc);
  const paramDefaulter = useParamDefaulter();

  const enrichedFilter = computed(() => {
    if (chart.value.filter === null) {
      return null;
    }
    // Restore scope arrays stripped by backend `omitempty` (pkg/xfmr/document/view.go).
    return paramDefaulter.setUserScopeTreeDefaults(paramEnricher.enrich(copy(chart.value.filter)));
  });

  const filter = computed(() => {
    switch (chart.value.source) {
      case goalConfig.model: {
        const { filter: goalCycleFilter, varBlocks: goalCycleVarBlocks } = createGoalCycleFilter(chart.value.goalCycles);
        if (enrichedFilter.value === null) {
          return {
            filterTree: combine(AND, [excludePlanningGoalsFilter, goalCycleFilter]),
            varBlocks: goalCycleVarBlocks,
          };
        }
        const filterObject = goalFilterHandler.Translate(enrichedFilter.value);
        return {
          filterTree: combine(AND, [excludePlanningGoalsFilter, goalCycleFilter, filterObject.filterTree]),
          varBlocks: [...filterObject.varBlocks, ...goalCycleVarBlocks],
        };
      }
      case updateConfig.model: {
        if (enrichedFilter.value === null) {
          return { filterTree: null, varBlocks: [] };
        }
        const filterObject = updatesFilterHandler.Translate(enrichedFilter.value);
        const checkInFilter = { op: NOT, child: [{ func: { name: HAS, attr: updateConfig.edges.goal } }] };
        return {
          filterTree: combine(AND, [checkInFilter, filterObject.filterTree]),
          varBlocks: filterObject.varBlocks,
        };
      }
      case goalActivityConfig.model: {
        if (enrichedFilter.value === null) {
          return { filterTree: null, varBlocks: [] };
        }
        const filterObject = goalActivityFilterHandler.Translate(enrichedFilter.value);
        return {
          filterTree: filterObject.filterTree,
          varBlocks: filterObject.varBlocks,
        };
      }
      default:
        return { filterTree: null, varBlocks: [] };
    }
  });

  const isGoalCycleGroupingWithFilter = computed(() => chart.value.source === goalConfig.model
    && !chart.value.groupBy.isProperty
    && chart.value.groupBy.edge === goalConfig.edges.goalCycle
    && chart.value.goalCycles.length > 0);

  const stackableChartTypes = [gridPageChartType.line, gridPageChartType.bar, gridPageChartType.column, gridPageChartType.table];

  const hasStacking = computed(() => stackableChartTypes.includes(chart.value.chartType)
    && chart.value.aggregationConfig.stackOptions.groupBy !== chartGroupByOption.none);

  const getEdge = (prop) => (prop.isProperty ? 'cachedProperty' : prop.edge);

  const getLangs = (prop) => (prop.isProperty && prop.property?.uid ? [`p${prop.property.uid}`] : null);

  // A chart with a deleted-property ref can't be aggregated or grouped
  // meaningfully — short-circuit the query so we don't build malformed Dgraph
  // blocks downstream. Resolves against the canonical refs for the source, so
  // both dynamic deletion (user removed a property) and direct removal (entry
  // removed from lib/<entity>/properties.js) surface here.
  const { isOrphanRef } = useChartProps(computed(() => chart.value.source));
  const hasOrphanRef = computed(() => isOrphanRef(chart.value.groupBy)
    || isOrphanRef(chart.value.aggregationConfig?.aggregation)
    || isOrphanRef(chart.value.aggregationConfig?.stackOptions));

  const isNumericAggregator = (agg) => [aggregatorType.avg, aggregatorType.sum, aggregatorType.min, aggregatorType.max].includes(agg);

  // Edges that are always set on every record — there are no rows missing them,
  // so the "unassigned" / "no value" query blocks can be skipped.
  const alwaysSetEdges = [CREATED_AT, MODIFIED_AT, goalConfig.edges.cachedCalculatedCurrent];
  const isAlwaysSet = (prop) => !prop.isProperty && alwaysSetEdges.includes(prop.edge);

  // Binds the aggregation predicate to a uid-scoped variable and aggregates
  // `val(var)` outside @groupby. Used by the @groupby path for non-stacked
  // numeric unassigned / metric; the property-numeric path uses raw rows.
  const buildVarValBlocks = ({ resultAlias, varName, filterTree }) => {
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    return [
      {
        alias: 'var',
        func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
        filter: filterTree,
        model: chart.value.source,
        children: [{
          attr: getEdge(chart.value.aggregationConfig.aggregation),
          langs: getLangs(chart.value.aggregationConfig.aggregation),
          var: varName,
        }],
      },
      {
        alias: resultAlias,
        children: [{
          alias: aggregator,
          attr: 'val',
          isInternal: true,
          needsVar: [{ name: varName, typ: 2 }],
          func: { name: aggregator, needsVar: [{ name: varName, typ: 2 }] },
        }],
      },
    ];
  };

  // var/val blocks don't return count; emit one alongside so the chart-grouping
  // reducer can weight the bucket against real groups (item.avg * undefined → NaN).
  const buildCountBlock = (alias, filterTree) => ({
    alias,
    func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
    filter: filterTree,
    model: chart.value.source,
    children: [{ attr: UID, isCount: true }],
  });

  const buildNotHasFilter = (groupBy) => {
    if (groupBy.isProperty && groupBy.property?.uid) {
      return { func: { name: EQ, attr: 'cachedProperty', lang: `p${groupBy.property.uid}`, args: [{ value: '' }] } };
    }
    return notHasFilter(groupBy.edge);
  };

  const buildUnassignedFilter = () => {
    const notHasFilter = buildNotHasFilter(chart.value.groupBy);

    // When grouping by goalCycle edge, the base filter includes the goalCycleFilter
    // which contradicts "NOT has(goalCycle)". Use only the user filter in that case,
    // but still exclude planning goals.
    if (chart.value.source === goalConfig.model
      && !chart.value.groupBy.isProperty
      && chart.value.groupBy.edge === goalConfig.edges.goalCycle) {
      const userFilterTree = enrichedFilter.value !== null
        ? goalFilterHandler.Translate(enrichedFilter.value).filterTree
        : null;
      return combine(AND, [excludePlanningGoalsFilter, userFilterTree, notHasFilter]);
    }

    return filter.value.filterTree
      ? combine(AND, [filter.value.filterTree, notHasFilter])
      : notHasFilter;
  };

  // Dgraph's @groupby parser rejects `val()` as an aggregator argument and
  // its executor refuses sum/avg/min/max on string @lang predicates — so
  // cachedProperty can't be aggregated server-side. For these aggregations we
  // fetch one row per goal and fold client-side.
  const isPropertyNumericAgg = computed(() => {
    const agg = chart.value.aggregationConfig.aggregation;
    return agg.isProperty === true && isNumericAggregator(agg.aggregator);
  });

  const propertyNumericPath = usePropertyNumericChartPath({
    chart,
    hasStacking,
    isAlwaysSet,
    isNumericAggregator,
    isGoalCycleGroupingWithFilter,
    buildNotHasFilter,
    getEdge,
    getLangs,
  });

  const buildChildrenForAggregator = (aggregator) => {
    switch (aggregator) {
      case aggregatorType.count:
      case aggregatorType.countDistinct:
        return [{ attr: UID, isCount: true }];
      case aggregatorType.avg:
      case aggregatorType.sum:
      case aggregatorType.min:
      case aggregatorType.max:
        return [
          { attr: UID, isCount: true },
          { alias: aggregator, attr: getEdge(chart.value.aggregationConfig.aggregation), func: { name: aggregator } },
        ];
      default:
        return null;
    }
  };

  const buildUnassignedQueryBlocks = () => {
    if (chart.value.groupBy.groupBy === chartGroupByOption.none) {
      return [];
    }

    // When grouping by goalCycle with a global cycle filter, goals without a cycle
    // cannot exist in the result set, so skip the unassigned query entirely.
    if (isGoalCycleGroupingWithFilter.value) {
      return [];
    }

    if (isAlwaysSet(chart.value.groupBy)) {
      return [];
    }

    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const isNumeric = isNumericAggregator(aggregator);
    const children = buildChildrenForAggregator(aggregator);
    if (!children) return [];

    const unassignedFilter = buildUnassignedFilter();
    const { stackOptions } = chart.value.aggregationConfig;

    if (isPropertyNumericAgg.value) {
      return propertyNumericPath.buildUnassignedBlocks(unassignedFilter, stackOptions);
    }

    const base = {
      alias: 'unassigned',
      func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
      filter: unassignedFilter,
      model: chart.value.source,
    };

    if (hasStacking.value) {
      base.isGroupby = true;
      base.groupbyAttrs = [{ alias: 'stackGroup', attr: getEdge(stackOptions), langs: getLangs(stackOptions) }];
      if (aggregator === aggregatorType.countDistinct) {
        base.groupbyAttrs.push({ alias: 'subGroup', attr: getEdge(chart.value.aggregationConfig.aggregation), langs: getLangs(chart.value.aggregationConfig.aggregation) });
      }
    }

    if (!hasStacking.value && isNumeric) {
      return [
        ...buildVarValBlocks({ resultAlias: 'unassigned', varName: 'unassignedVal', filterTree: unassignedFilter }),
        buildCountBlock('unassignedCount', unassignedFilter),
      ];
    }

    const blocks = [{ ...base, children }];

    // Items missing both the main groupBy attr AND the stack attr
    if (hasStacking.value && !isAlwaysSet(stackOptions)) {
      const notHasStackFilter = buildNotHasFilter(stackOptions);
      const noStackUnassignedFilter = combine(AND, [unassignedFilter, notHasStackFilter]);

      if (isNumeric) {
        blocks.push(
          ...buildVarValBlocks({ resultAlias: 'unassignedNoStack', varName: 'unassignedNoStackVal', filterTree: noStackUnassignedFilter }),
          buildCountBlock('unassignedNoStackCount', noStackUnassignedFilter),
        );
      } else {
        blocks.push(buildCountBlock('unassignedNoStack', noStackUnassignedFilter));
      }
    }

    return blocks;
  };

  // Folds the @groupby unassigned bucket into a single UNASSIGNED_VALUE entry
  // with count (from the parallel `unassignedCount` block) so the reducer can
  // weight it against real groups.
  const buildGroupByUnassignedEntry = (data, unassignedValue) => {
    if (typeof unassignedValue !== 'number') return null;
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const count = data.unassignedCount?.[0]?.count ?? 0;
    if (count === 0) return null;
    return { val: UNASSIGNED_VALUE, count, [aggregator]: unassignedValue };
  };

  const decodeGroupByUnassignedValue = (data) => {
    if (isAlwaysSet(chart.value.groupBy)) return null;
    const { aggregator } = chart.value.aggregationConfig.aggregation;

    if (hasStacking.value) {
      const entries = [...(data.unassigned?.[0]?.['@groupby'] ?? [])];
      const noStackRaw = data.unassignedNoStack?.[0];
      if (noStackRaw) {
        const isCountAgg = aggregator === aggregatorType.count || aggregator === aggregatorType.countDistinct;
        const hasValue = isCountAgg
          ? (noStackRaw.count ?? 0) > 0
          : noStackRaw[aggregator] != null;
        if (hasValue) {
          // var/val blocks don't return count; pull it from the parallel block.
          const entry = { ...noStackRaw, stackGroup: UNASSIGNED_VALUE };
          if (!isCountAgg) entry.count = data.unassignedNoStackCount?.[0]?.count ?? 0;
          entries.push(entry);
        }
      }
      return entries;
    }

    const raw = data.unassigned?.[0];
    if (!raw || aggregator === aggregatorType.countDistinct) return 0;
    return raw[aggregator] ?? 0;
  };

  const decodeGroupByChartData = (data) => {
    const hasUnassignedQuery = chart.value.groupBy.groupBy !== chartGroupByOption.none
      && !isGoalCycleGroupingWithFilter.value;
    const unassignedValue = hasUnassignedQuery ? decodeGroupByUnassignedValue(data) : null;
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const unassignedEntry = !hasStacking.value && isNumericAggregator(aggregator)
      ? buildGroupByUnassignedEntry(data, unassignedValue)
      : null;
    return {
      groupByData: [
        ...(data.res?.[0]?.['@groupby'] ?? []),
        ...(unassignedEntry ? [unassignedEntry] : []),
      ],
      unassignedValue,
      noStackData: data.resNoStack?.[0]?.['@groupby'] ?? [],
    };
  };

  // Items that have the main groupBy attr but NOT the stack attr (per-category "No value" stack)
  const buildNoStackQueryBlocks = () => {
    if (!hasStacking.value) return [];
    const { stackOptions } = chart.value.aggregationConfig;

    if (isAlwaysSet(stackOptions)) return [];

    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const children = buildChildrenForAggregator(aggregator);
    if (!children) return [];

    const notHasStackFilter = buildNotHasFilter(stackOptions);
    const noStackFilter = filter.value.filterTree
      ? combine(AND, [filter.value.filterTree, notHasStackFilter])
      : notHasStackFilter;

    if (isPropertyNumericAgg.value) {
      return [propertyNumericPath.buildNoStackBlock(noStackFilter)];
    }

    const valGroupbyAttrs = [{
      alias: 'val',
      attr: getEdge(chart.value.groupBy),
      langs: getLangs(chart.value.groupBy),
    }];

    const block = {
      alias: 'resNoStack',
      func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
      filter: noStackFilter,
      model: chart.value.source,
      isGroupby: true,
      groupbyAttrs: valGroupbyAttrs,
      children,
    };

    if (aggregator === aggregatorType.countDistinct) {
      block.groupbyAttrs = [
        ...valGroupbyAttrs,
        { alias: 'subGroup', attr: getEdge(chart.value.aggregationConfig.aggregation), langs: getLangs(chart.value.aggregationConfig.aggregation) },
      ];
    }

    return [block];
  };

  const appendStackOptions = (groupbyAttrs) => {
    if (!hasStacking.value) {
      return groupbyAttrs;
    }
    const { stackOptions } = chart.value.aggregationConfig;
    return [
      ...groupbyAttrs,
      { alias: 'stackGroup', attr: getEdge(stackOptions), langs: getLangs(stackOptions) },
    ];
  };

  const isTimeSeriesMode = computed(() => TIME_SERIES_AGGREGATORS.includes(chart.value.aggregationConfig?.aggregation?.aggregator));

  const timeSeriesData = computed(() => {
    if (!isTimeSeriesMode.value || chart.value.goalCycles.length === 0) {
      return { timeSeries: [], startDate: null, endDate: null };
    }

    const cycles = chart.value.goalCycles;
    const startDate = cycles.reduce((min, cycle) => {
      const cycleStart = DateTime.fromISO(cycle.start);
      return min === null || cycleStart < min ? cycleStart : min;
    }, null);

    const endDate = cycles.reduce((max, cycle) => {
      const cycleEnd = DateTime.fromISO(cycle.end);
      return max === null || cycleEnd > max ? cycleEnd : max;
    }, null);

    if (!startDate || !endDate) {
      return { timeSeries: [], startDate: null, endDate: null };
    }

    const interval = chart.value.aggregationConfig.timeSeriesInterval || 'weekly';
    const timeSeries = generateTimeSeries(startDate, endDate, interval, stableNow.value);

    return { timeSeries, startDate, endDate };
  });

  const query = computed(() => {
    if (hasOrphanRef.value) {
      return [];
    }
    if (isTimeSeriesMode.value) {
      const { timeSeries } = timeSeriesData.value;
      if (timeSeries.length === 0) {
        return [];
      }

      const cycleGoalsVar = 'cycle_goals';
      const goalCycles = chart.value.goalCycles;
      const goalFilter = enrichedFilter.value !== null
        ? goalFilterHandler.Translate(enrichedFilter.value)
        : { filterTree: null, varBlocks: [] };
      const filterTree = combine(AND, [excludePlanningGoalsFilter, goalFilter.filterTree]);

      return [
        ...goalsInCycleVarBlock({ goalCycles, filter: filterTree, varName: cycleGoalsVar }),
        ...goalFilter.varBlocks,
        {
          alias: RESULT,
          model: goalConfig.model,
          needsVar: [{ name: cycleGoalsVar, typ: 1 }],
          func: { name: UID, needsVar: [{ name: cycleGoalsVar, typ: 1 }] },
          filter: filterTree,
          default: [],
          children: [
            buildCustomFuncProgressCourseListPred({
              timeSeries,
              attr: UID,
              alias: customFunc.progressCourseList,
              useBoundedProgress: chart.value.aggregationConfig.aggregation.aggregator !== aggregatorType.progressOverTimeUnbounded,
            }),
          ],
        },
      ];
    }
    switch (chart.value.chartType) {
      case gridPageChartType.metric:
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count: {
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                children: [
                  { attr: UID, isCount: true },
                ],
              },
            ];
          }
          case aggregatorType.countDistinct:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: [{
                  attr: getEdge(chart.value.aggregationConfig.aggregation),
                  langs: getLangs(chart.value.aggregationConfig.aggregation),
                }],
              },
            ];
          case aggregatorType.avg:
          case aggregatorType.sum:
          case aggregatorType.min:
          case aggregatorType.max: {
            if (isPropertyNumericAgg.value) {
              return [
                ...filter.value.varBlocks,
                propertyNumericPath.buildMetricBlock(filter.value.filterTree),
              ];
            }
            return [
              ...filter.value.varBlocks,
              ...buildVarValBlocks({ resultAlias: 'res', varName: 'edgeVal', filterTree: filter.value.filterTree }),
            ];
          }
          default:
            return [];
        }
      case gridPageChartType.pie:
      case gridPageChartType.line:
      case gridPageChartType.bar:
      case gridPageChartType.table:
      case gridPageChartType.column: {
        const unassignedBlocks = buildUnassignedQueryBlocks();
        const noStackBlocks = buildNoStackQueryBlocks();
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([{
                  alias: 'val',
                  attr: getEdge(chart.value.groupBy),
                  langs: getLangs(chart.value.groupBy),
                }]),
                children: [
                  { attr: UID, isCount: true },
                ],
              },
              ...noStackBlocks,
              ...unassignedBlocks,
            ];
          case aggregatorType.countDistinct:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([
                  {
                    alias: 'val',
                    attr: getEdge(chart.value.groupBy),
                    langs: getLangs(chart.value.groupBy),
                  },
                  {
                    alias: 'subGroup',
                    attr: getEdge(chart.value.aggregationConfig.aggregation),
                    langs: getLangs(chart.value.aggregationConfig.aggregation),
                  },
                ]),
                children: [
                  { attr: UID, isCount: true },
                ],
              },
              ...noStackBlocks,
              ...unassignedBlocks,
            ];
          case aggregatorType.avg:
          case aggregatorType.sum:
          case aggregatorType.min:
          case aggregatorType.max: {
            if (isPropertyNumericAgg.value) {
              return [
                ...filter.value.varBlocks,
                propertyNumericPath.buildResBlock(filter.value.filterTree),
                ...noStackBlocks,
                ...unassignedBlocks,
              ];
            }
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([{
                  alias: 'val',
                  attr: getEdge(chart.value.groupBy),
                  langs: getLangs(chart.value.groupBy),
                }]),
                children: buildChildrenForAggregator(chart.value.aggregationConfig.aggregation.aggregator),
              },
              ...noStackBlocks,
              ...unassignedBlocks,
            ];
          }
          default:
            return [];
        }
      }
      default:
        return [];
    }
  });

  const decodeTimeSeries = (progressCourseList) => {
    const aggregator = chart.value.aggregationConfig?.aggregation?.aggregator;
    const base = {
      isTimeSeries: true,
      progressData: [],
      statusScoreData: [],
      statusDistributionData: [],
      startDate: timeSeriesData.value.startDate,
      endDate: timeSeriesData.value.endDate,
    };

    if (!progressCourseList || progressCourseList.length === 0) {
      return base;
    }

    if (aggregator === aggregatorType.statusScoreOverTime) {
      base.statusScoreData = progressCourseList.map((point) => {
        const date = DateTime.fromISO(point.date);
        const list = point.list ?? [];
        if (list.length === 0) {
          return { x: date.toMillis(), y: 0 };
        }
        const avg = list.reduce((acc, g) => acc + (g.statusOption?.score ?? 0), 0) / list.length;
        return { x: date.toMillis(), y: Math.round(avg * 10) / 10 };
      });
      return base;
    }

    if (aggregator === aggregatorType.statusDistributionOverTime) {
      base.statusDistributionData = progressCourseList.map((point) => {
        const date = DateTime.fromISO(point.date);
        const statusCounts = {};
        (point.list ?? []).forEach((g) => {
          const opt = g.statusOption;
          if (!opt?.uid) {
            return;
          }
          statusCounts[opt.uid] = (statusCounts[opt.uid] || 0) + 1;
        });
        return { x: date.toMillis(), statusCounts };
      });
      return base;
    }

    base.progressData = progressCourseList.map((point) => {
      const date = DateTime.fromISO(point.date);
      const progressValues = point.list?.map((g) => g.progress) ?? [];
      const avgProgress = progressValues.length > 0
        ? progressValues.reduce((sum, p) => sum + p, 0) / progressValues.length
        : 0;
      return { x: date.toMillis(), y: Math.round(avgProgress) };
    });
    return base;
  };

  const decodeResponse = (data) => {
    if (isTimeSeriesMode.value) {
      return decodeTimeSeries(data.result?.[0]?.progressCourseList);
    }

    switch (chart.value.chartType) {
      case gridPageChartType.metric: {
        const { aggregator } = chart.value.aggregationConfig.aggregation;
        if (isPropertyNumericAgg.value && isNumericAggregator(aggregator)) {
          return propertyNumericPath.decodeMetric(data, aggregator);
        }
        if (aggregator === aggregatorType.countDistinct) {
          return data.res?.[0]?.['@groupby']?.length ?? 0;
        }
        return data.res?.[0]?.[aggregator] ?? 0;
      }
      case gridPageChartType.pie:
      case gridPageChartType.line:
      case gridPageChartType.bar:
      case gridPageChartType.table:
      case gridPageChartType.column: {
        if (Object.keys(data).length === 0) return { series: [], total: 0 };
        const { groupByData, unassignedValue, noStackData } = isPropertyNumericAgg.value
          ? propertyNumericPath.decodeChartData(data)
          : decodeGroupByChartData(data);
        if (groupByData.length === 0 && unassignedValue === null && noStackData.length === 0) {
          return { series: [], total: 0 };
        }
        const { aggregator } = chart.value.aggregationConfig.aggregation;
        const supported = [
          aggregatorType.count, aggregatorType.avg, aggregatorType.sum,
          aggregatorType.min, aggregatorType.max, aggregatorType.countDistinct,
        ].includes(aggregator);
        return supported
          ? processChartData(groupByData, unassignedValue, noStackData)
          : { series: [], total: 0 };
      }
      default:
        return { series: [], total: 0 };
    }
  };

  const result = computed(() => {
    if (resultRaw.value === null || loading.value) {
      return null;
    }
    return decodeResponse(resultRaw.value);
  });

  const runQuery = (debounceDelay = 0) => {
    refreshStableNow();
    loading.value = true;
    const run = () => {
      if (query.value.length === 0) {
        loading.value = false;
        return;
      }
      dogma.query(query.value)
        .then((res) => {
          // Frozen: response is never mutated, and freezing keeps V8's hidden
          // classes stable across the many reads from processChartData.
          resultRaw.value = res.data ? Object.freeze(res.data) : res.data;
          error.value = null;
        })
        .catch(logCatch((e) => {
          error.value = e;
        }))
        .finally(() => {
          loading.value = false;
        });
    };
    debounce(run, debounceDelay);
  };

  watch(query, (newVal, oldVal) => {
    if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
      return;
    }
    runQuery(50);
  }, { deep: true });

  runQuery();

  return {
    runQuery,
    result,
    error,
    loading,
    hasOrphanRef,
  };
}
